import { callZohoAPI } from '../utils/api'
import { errorLogService } from './errorLog'

// Zoho Creator API paths
const CREATOR_BASE = 'creator/v2.1/data/52studios/campaigns'
const SIGNUP_FORM = `${CREATOR_BASE}/form/Sign_Up_Forms`
const SIGNUP_REPORT = `${CREATOR_BASE}/report/Sign_Up_Forms_Report`
const EMAIL_FORM = `${CREATOR_BASE}/form/Email_Template`
const EMAIL_REPORT = `${CREATOR_BASE}/report/All_Email_Templates`
const FORM_CONFIG_FORM = `${CREATOR_BASE}/form/Form_Configurations`
const FORM_CONFIG_REPORT = `${CREATOR_BASE}/report/All_Form_Configurations`
const NEWSLETTER_FORM = `${CREATOR_BASE}/form/Newsletters`
const NEWSLETTER_REPORT = `${CREATOR_BASE}/report/All_Newsletters`
const SUBMISSION_FORM = `${CREATOR_BASE}/form/Submissions`
const SUBMISSION_REPORT = `${CREATOR_BASE}/report/All_Submissions`
const TEAM_FORM = `${CREATOR_BASE}/form/Team_Configurations`
const TEAM_REPORT = `${CREATOR_BASE}/report/All_Team_Configurations`
const CAMPAIGN_FORM = `${CREATOR_BASE}/form/Campaigns`
const CAMPAIGN_REPORT = `${CREATOR_BASE}/report/All_Campaigns`
const USER_FORM = `${CREATOR_BASE}/form/Users`
const USER_REPORT = `${CREATOR_BASE}/report/All_Users`
const ROLE_FORM = `${CREATOR_BASE}/form/Roles`
const ROLE_REPORT = `${CREATOR_BASE}/report/All_Roles`
const AUDIENCE_FORM = `${CREATOR_BASE}/form/Audiences`
const AUDIENCE_REPORT = `${CREATOR_BASE}/report/All_Audiences`
const FORM_TEMPLATE_FORM = `${CREATOR_BASE}/form/Form_Templates`
const FORM_TEMPLATE_REPORT = `${CREATOR_BASE}/report/All_Form_Templates`
const EMAIL_LIBRARY_FORM = `${CREATOR_BASE}/form/Email_Template_Library`
const EMAIL_LIBRARY_REPORT = `${CREATOR_BASE}/report/All_Email_Template_Libraries`
const LANDING_PAGE_FORM = `${CREATOR_BASE}/form/Landing_Pages`
const LANDING_PAGE_REPORT = `${CREATOR_BASE}/report/All_Landing_Pages`

// ── Types ──

export interface SignupFormRecord {
  ID: string
  Form_Name: string
  Url_Slug: string
  Status: string
  HTML: string
  CSS: string
  JavaScript1: string
  JavaScript_Embed: string
  Iframe_Embed: string
  Team: string
  Region: string
  Builder_Schema?: string
  Builder_Mode?: string
}

export interface EmailTemplateRecord {
  ID: string
  Email_Subject: string
  Preheader_Text: string
  Email_HTML_Body: string
  Confirmation_Link: string
  Sign_Up_Form: string
  Team: string
  Region: string
  Builder_Schema?: string
  Builder_Mode?: string
}

export interface FormConfigRecord {
  ID: string
  Sign_Up_Form: string
  Email_Template: string
  Team: string
  Region: string
}

// Newsletter subform row types
export interface NewsletterTagRow {
  Tag_ID: string
  Tag_Name: string
}

export interface NewsletterCategoryRuleRow {
  Form_Field: string
  Condition: string
  Value: string
  Category_Name: string
  Category_ID: string
}

export interface NewsletterMailingListRow {
  Mailing_List_Name: string
  Mailing_List_ID: string
}

export interface NewsletterRecord {
  ID: string
  Newsletter: string
  Newsletter_ID: string
  Sign_Up_Form: string
  Team: string
  Region: string
  // Mailing_List_Name: string // REMOVED - replaced by subform
  // Mailing_List_ID: string   // REMOVED - replaced by subform
  Mailing_Lists?: NewsletterMailingListRow[] // New subform
  Newsletter_Tags?: NewsletterTagRow[]
  Newsletter_Category_Rules?: NewsletterCategoryRuleRow[]
}

// Submission types
export interface SubmissionRecord {
  ID: string
  Form_Payload: string
  Status: string
  Newsletter_ID: string
  Team: string
  Region: string
  Confirmation_Token?: string
  Confirmed_At?: string
  Confirmed_At1?: string // New field for Confirmed At
  Redirect_URL?: string
  Rendered_Email_HTML?: string
  Rendered_Email_Subject?: string
  Subscriber_Email?: string
  Added_Time?: string
}

// Team types
export interface TeamRegionRow {
  Region_Name: string
}

export interface TeamRecord {
  ID: string
  Team_Name: string
  Team_ID: string
  Logo_Url?: string
  Primary_Color?: string
  Team_Regions?: TeamRegionRow[]
}

// Campaign types
export interface CampaignTargetRegionRow {
  Region_Name: string
}

export interface CampaignTargetMailingListRow {
  Mailing_List_ID: string
  Mailing_List_Name: string
}

export interface CampaignTargetTagRow {
  Tag_ID: string
  Tag_Name: string
}

export interface CampaignRecord {
  ID: string
  Campaign_Name: string
  Campaign_ID: string
  Email_Subject: string
  Preheader_Text: string
  From_Name: string
  From_Email: string
  HTML_Content: string
  Text_Content: string
  Status: string // "Draft" | "Exported" | "Sent"
  Bucket_ID: string
  Bucket_Expires_At: string
  Recipient_Count: number
  Exported_At?: string // Exists in Creator form but not sent on create
  Sent_At?: string
  Team: string
  Region: string
  Campaign_Target_Regions?: CampaignTargetRegionRow[]
  Campaign_Target_Mailing_Lists?: CampaignTargetMailingListRow[]
  Campaign_Target_Tags?: CampaignTargetTagRow[]
}

// User types
export interface UserRegionRow {
  Region_Name: string
}

export interface UserRecord {
  ID: string
  User_Name: string
  Email: string
  Role: 'Super Admin' | 'Team Manager' | 'Operator' | 'Viewer'
  Team: string
  Status: 'Active' | 'Suspended' | 'Invited'
  Invited_By: string
  Created_At: string
  Last_Login: string
  User_Regions?: UserRegionRow[]
}

// Role types
export interface RolePermissionRow {
  Permission_Key: string
}

export interface RoleRecord {
  ID: string
  Role_Name: string
  Description: string
  Is_System: boolean
  Role_Permissions?: RolePermissionRow[]
}

// Audience types
export interface AudienceRecord {
  ID: string
  Audience_ID: string
  Audience_Name: string
  Description: string
  Team: string
  Region: string
  Rules_JSON: string
  Logic: string
  Last_Count: number
}

// Form Template Library types
export interface FormTemplateRecord {
  ID: string
  Template_Name: string
  Description: string
  HTML: string
  CSS: string
  JavaScript1: string
  Builder_Schema: string
  Builder_Mode: string
  Category: string
  Team: string
  Region: string
}

// Email Template Library types
export interface EmailLibraryRecord {
  ID: string
  Template_Name: string
  Description: string
  Email_HTML_Body: string
  Email_Subject: string
  Preheader_Text: string
  Builder_Schema: string
  Builder_Mode: string
  Category: string
  Team: string
  Region: string
}

// Landing Page types
export interface LandingPageRecord {
  ID: string
  Page_Name: string
  Url_Slug: string
  Status: string
  HTML: string
  CSS: string
  JavaScript1: string
  Builder_Schema?: string
  Builder_Mode?: string
  Team: string
  Region: string
}

// ── Helpers ──

function isNoRecordsError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return (
    msg.includes('3100') ||
    msg.includes('9220') ||
    msg.toLowerCase().includes('no records')
  )
}

function handleListResponse<T>(response: any): T[] {
  if (!response) return []
  if (response.code === 3100 || response.code === 9220) return []
  if (response.data && Array.isArray(response.data)) return response.data as T[]
  return []
}

function logAndThrow(action: string, err: unknown, payload?: any): never {
  const message = err instanceof Error ? err.message : 'Unknown error'
  const debug = (err as any)?.debug
  errorLogService.log(action, message, {
    ...(payload ? { sentPayload: payload } : {}),
    ...(debug ? { debug } : {}),
  })
  throw err
}

// ── Sign Up Forms CRUD ──

export const signupFormsApi = {
  list: async (): Promise<SignupFormRecord[]> => {
    try {
      const response = await callZohoAPI(SIGNUP_REPORT)
      return handleListResponse<SignupFormRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Signup Forms', err)
    }
  },

  get: async (id: string): Promise<SignupFormRecord | null> => {
    try {
      const response = await callZohoAPI(`${SIGNUP_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Signup Form', err, { id })
    }
  },

  findBySlug: async (slug: string): Promise<SignupFormRecord | null> => {
    try {
      // Try criteria search first (more efficient)
      const criteria = `Url_Slug == "${slug}"`
      const url = `${SIGNUP_REPORT}?criteria=${encodeURIComponent(criteria)}`
      const response = await callZohoAPI(url)
      const records = handleListResponse<SignupFormRecord>(response)
      if (records.length > 0) return records[0]

      // Fallback to list all if criteria fails (sometimes happens with special chars)
      const all = await signupFormsApi.list()
      return all.find((f) => f.Url_Slug === slug) || null
    } catch {
      return null
    }
  },

  create: async (data: Partial<Omit<SignupFormRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(SIGNUP_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Signup Form', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<SignupFormRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${SIGNUP_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Signup Form', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${SIGNUP_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Signup Form', err, { id })
    }
  },
}

// ── Email Templates CRUD ──

export const emailTemplatesApi = {
  list: async (): Promise<EmailTemplateRecord[]> => {
    try {
      const response = await callZohoAPI(EMAIL_REPORT)
      return handleListResponse<EmailTemplateRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Email Templates', err)
    }
  },

  get: async (id: string): Promise<EmailTemplateRecord | null> => {
    try {
      const response = await callZohoAPI(`${EMAIL_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Email Template', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<EmailTemplateRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(EMAIL_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Email Template', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<EmailTemplateRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${EMAIL_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Email Template', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${EMAIL_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Email Template', err, { id })
    }
  },
}

// ── Form Configurations CRUD ──

export const formConfigsApi = {
  list: async (): Promise<FormConfigRecord[]> => {
    try {
      // field_config=all ensures lookup fields return display_value + ID (not just ID)
      const response = await callZohoAPI(
        `${FORM_CONFIG_REPORT}?field_config=all`,
      )
      return handleListResponse<FormConfigRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Form Configurations', err)
    }
  },

  get: async (id: string): Promise<FormConfigRecord | null> => {
    try {
      // field_config=all ensures lookup fields return display_value + ID
      const response = await callZohoAPI(
        `${FORM_CONFIG_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Form Configuration', err, { id })
    }
  },

  findByTemplateId: async (
    templateId: string,
  ): Promise<FormConfigRecord | null> => {
    try {
      const all = await formConfigsApi.list()
      return (
        all.find((c) => {
          const id = extractLookupId(c.Email_Template)
          return id === templateId
        }) || null
      )
    } catch {
      return null
    }
  },

  findByFormId: async (formId: string): Promise<FormConfigRecord[]> => {
    try {
      const all = await formConfigsApi.list()
      return all.filter((c) => {
        const id = extractLookupId(c.Sign_Up_Form)
        return id === formId
      })
    } catch {
      return []
    }
  },

  create: async (data: Partial<Omit<FormConfigRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(FORM_CONFIG_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Form Configuration', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<FormConfigRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_CONFIG_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Form Configuration', err, { id, ...data })
    }
  },

  upsertByTemplate: async (
    templateId: string,
    formId: string,
    team: string,
  ): Promise<{ action: 'created' | 'updated' }> => {
    const existing = await formConfigsApi.findByTemplateId(templateId)
    const payload = {
      Sign_Up_Form: formId,
      Email_Template: templateId,
      Team: team,
    }
    if (existing) {
      await formConfigsApi.update(existing.ID, payload)
      return { action: 'updated' }
    } else {
      await formConfigsApi.create(payload)
      return { action: 'created' }
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_CONFIG_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Form Configuration', err, { id })
    }
  },
}

// ── Newsletters CRUD ──

export const newslettersApi = {
  list: async (): Promise<NewsletterRecord[]> => {
    try {
      const response = await callZohoAPI(NEWSLETTER_REPORT)
      return handleListResponse<NewsletterRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Newsletters', err)
    }
  },

  get: async (id: string): Promise<NewsletterRecord | null> => {
    try {
      const response = await callZohoAPI(`${NEWSLETTER_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Newsletter', err, { id })
    }
  },

  create: async (data: Partial<Omit<NewsletterRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(NEWSLETTER_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Newsletter', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<NewsletterRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${NEWSLETTER_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Newsletter', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${NEWSLETTER_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Newsletter', err, { id })
    }
  },

  generateId: (): string => {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 8)
    return `nl_${timestamp}_${random}`
  },
}

// ── Submissions CRUD ──

export const submissionsApi = {
  list: async (maxRecords?: number): Promise<SubmissionRecord[]> => {
    try {
      const url = maxRecords
        ? `${SUBMISSION_REPORT}?max_records=${maxRecords}`
        : SUBMISSION_REPORT
      const response = await callZohoAPI(url)
      return handleListResponse<SubmissionRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Submissions', err)
    }
  },

  get: async (id: string): Promise<SubmissionRecord | null> => {
    try {
      // field_config=all ensures Redirect_URL, Newsletter_ID and all fields are returned
      const response = await callZohoAPI(
        `${SUBMISSION_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Submission', err, { id })
    }
  },

  create: async (data: Partial<Omit<SubmissionRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(SUBMISSION_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Submission', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<SubmissionRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      // SAFETY: Never send Confirmed_At to Zoho — it rejects all date formats we send
      const sanitized = { ...data } as Record<string, any>
      delete sanitized.Confirmed_At
      delete sanitized.confirmed_at

      const response = await callZohoAPI(
        `${SUBMISSION_REPORT}/${id}`,
        'PATCH',
        { data: sanitized },
      )
      return response
    } catch (err) {
      logAndThrow('Update Submission', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${SUBMISSION_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Submission', err, { id })
    }
  },

  findByNewsletterId: async (
    newsletterId: string,
  ): Promise<SubmissionRecord[]> => {
    try {
      const all = await submissionsApi.list(200)
      return all.filter((s) => s.Newsletter_ID === newsletterId)
    } catch {
      return []
    }
  },

  findByToken: async (token: string): Promise<SubmissionRecord | null> => {
    // Strategy 1: Try criteria search with field_config=all to ensure all fields returned
    try {
      const criteriaRaw = `Confirmation_Token == "${token}"`
      const criteriaEncoded = encodeURIComponent(criteriaRaw)
      const url = `${SUBMISSION_REPORT}?criteria=${criteriaEncoded}&field_config=all`
      console.log('findByToken [criteria]: URL =', url)
      const response = await callZohoAPI(url)
      console.log(
        'findByToken [criteria]: response =',
        JSON.stringify(response),
      )
      const records = handleListResponse<SubmissionRecord>(response)
      if (records.length > 0) {
        console.log(
          'findByToken [criteria]: found record ID =',
          records[0].ID,
          'token =',
          records[0].Confirmation_Token,
        )
        return records[0]
      }
      console.log('findByToken [criteria]: no records matched')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.warn('findByToken [criteria]: failed -', msg)
    }

    // Strategy 2: Fallback — fetch all submissions with field_config=all and filter client-side
    try {
      console.log(
        'findByToken [fallback]: fetching all submissions with field_config=all...',
      )
      const url = `${SUBMISSION_REPORT}?max_records=200&field_config=all`
      const response = await callZohoAPI(url)
      const all = handleListResponse<SubmissionRecord>(response)
      console.log('findByToken [fallback]: fetched', all.length, 'records')

      // Log all tokens for debugging
      const tokens = all.map((s) => ({ id: s.ID, token: s.Confirmation_Token }))
      console.log(
        'findByToken [fallback]: available tokens:',
        JSON.stringify(tokens),
      )

      const match = all.find((s) => s.Confirmation_Token === token)
      if (match) {
        console.log('findByToken [fallback]: exact match found, ID =', match.ID)
        return match
      }

      // Try trimmed comparison in case of whitespace
      const trimMatch = all.find(
        (s) => s.Confirmation_Token?.trim() === token.trim(),
      )
      if (trimMatch) {
        console.log(
          'findByToken [fallback]: trimmed match found, ID =',
          trimMatch.ID,
        )
        return trimMatch
      }

      console.log('findByToken [fallback]: no match found for token:', token)
      return null
    } catch (err) {
      console.error('findByToken [fallback]: failed -', err)
      return null
    }
  },

  confirm: async (
    token: string,
    recordId?: string,
  ): Promise<{
    success: boolean
    error?: string
    errorDetail?: string
    submission?: SubmissionRecord
  }> => {
    try {
      console.log('=== CONFIRM v7 === token:', token, 'recordId:', recordId)

      let submission: SubmissionRecord | null = null

      // Strategy A: If record ID is provided, fetch directly — most reliable
      if (recordId) {
        try {
          console.log('v7: Fetching by record ID:', recordId)
          const fetched = await submissionsApi.get(recordId)
          if (fetched) {
            // Verify token matches
            if (
              fetched.Confirmation_Token &&
              fetched.Confirmation_Token.trim() !== token.trim()
            ) {
              console.warn(
                'v7: Token mismatch! URL token:',
                token,
                'Record token:',
                fetched.Confirmation_Token,
              )
              return {
                success: false,
                error: 'invalid_token',
                errorDetail: `Token mismatch for record ${recordId}.`,
              }
            }
            submission = fetched
            console.log(
              'v7: Found by ID, Status:',
              submission.Status,
              'Redirect_URL:',
              submission.Redirect_URL,
            )
          }
        } catch (fetchErr) {
          console.warn('v7: Direct fetch by ID failed:', fetchErr)
        }
      }

      // Strategy B: Fall back to token search if no ID or ID fetch failed
      if (!submission) {
        console.log('v7: Falling back to findByToken...')
        submission = await submissionsApi.findByToken(token)
      }

      if (!submission) {
        return {
          success: false,
          error: 'invalid_token',
          errorDetail: `No submission found for token "${token}"${recordId ? ` (ID: ${recordId})` : ''}.`,
        }
      }

      console.log(
        'v7: Submission found, ID:',
        submission.ID,
        'Status:',
        submission.Status,
      )

      if (submission.Status === 'Confirmed') {
        console.log('v7: Already confirmed')
        return { success: true, submission }
      }

      // PATCH status to Confirmed
      try {
        console.log('v7: PATCHing Status=Confirmed for ID:', submission.ID)
        const result = await callZohoAPI(
          `${SUBMISSION_REPORT}/${submission.ID}`,
          'PATCH',
          { data: { Status: 'Confirmed' } },
        )
        console.log('v7: PATCH result:', JSON.stringify(result))
      } catch (updateErr: any) {
        const msg =
          updateErr instanceof Error ? updateErr.message : String(updateErr)
        console.error('v7: PATCH failed:', msg)
        errorLogService.log('Confirm Submission', msg, {
          sentPayload: { Status: 'Confirmed' },
          debug: updateErr?.debug,
        })
        return {
          success: false,
          error: 'server_error',
          errorDetail: `Update failed for ${submission.ID}: ${msg}`,
        }
      }

      return {
        success: true,
        submission: { ...submission, Status: 'Confirmed' },
      }
    } catch (err: any) {
      const detail = err instanceof Error ? err.message : String(err)
      console.error('v7: ERROR', detail)
      return { success: false, error: 'server_error', errorDetail: detail }
    }
  },
}

// ── Teams CRUD ──

export const teamsApi = {
  list: async (): Promise<TeamRecord[]> => {
    try {
      const response = await callZohoAPI(TEAM_REPORT)
      return handleListResponse<TeamRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Teams', err)
    }
  },

  get: async (id: string): Promise<TeamRecord | null> => {
    try {
      const response = await callZohoAPI(`${TEAM_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Team', err, { id })
    }
  },

  findByTeamName: async (teamName: string): Promise<TeamRecord | null> => {
    try {
      const all = await teamsApi.list()
      return all.find((t) => t.Team_Name === teamName) || null
    } catch {
      return null
    }
  },

  findByTeamId: async (teamId: string): Promise<TeamRecord | null> => {
    try {
      const all = await teamsApi.list()
      return all.find((t) => t.Team_ID === teamId) || null
    } catch {
      return null
    }
  },

  create: async (data: Partial<Omit<TeamRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(TEAM_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Team', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<TeamRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${TEAM_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Team', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${TEAM_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Team', err, { id })
    }
  },

  generateTeamId: (teamName: string): string => {
    return teamName
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
  },
}

// ── Campaigns CRUD ──

export const campaignsApi = {
  list: async (): Promise<CampaignRecord[]> => {
    try {
      // field_config=all ensures ALL form fields are returned, not just quick_view fields
      // See: https://www.zoho.com/creator/help/api/v2.1/get-records.html
      const response = await callZohoAPI(`${CAMPAIGN_REPORT}?field_config=all`)
      return handleListResponse<CampaignRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Campaigns', err)
    }
  },

  get: async (id: string): Promise<CampaignRecord | null> => {
    try {
      // field_config=all ensures ALL form fields are returned
      const response = await callZohoAPI(
        `${CAMPAIGN_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Campaign', err, { id })
    }
  },

  create: async (data: Partial<Omit<CampaignRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(CAMPAIGN_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Campaign', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<CampaignRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${CAMPAIGN_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Campaign', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${CAMPAIGN_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Campaign', err, { id })
    }
  },

  generateCampaignId: (): string => {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 8)
    return `cmp_${timestamp}_${random}`
  },

  generateBucketId: (teamId: string): string => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const randomSuffix = String.fromCharCode(
      65 + Math.floor(Math.random() * 26),
    )
    return `${teamId.toUpperCase()}_${year}_${month}_${day}_${randomSuffix}`
  },
}

// ── Users CRUD ──

export const usersApi = {
  list: async (): Promise<UserRecord[]> => {
    try {
      const response = await callZohoAPI(USER_REPORT)
      return handleListResponse<UserRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Users', err)
    }
  },

  get: async (id: string): Promise<UserRecord | null> => {
    try {
      const response = await callZohoAPI(`${USER_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get User', err, { id })
    }
  },

  findByTeam: async (teamName: string): Promise<UserRecord[]> => {
    try {
      const all = await usersApi.list()
      return all.filter((u) => u.Team === teamName)
    } catch {
      return []
    }
  },

  create: async (data: Partial<Omit<UserRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(USER_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create User', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<UserRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${USER_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update User', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${USER_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete User', err, { id })
    }
  },
}

// ── Roles CRUD ──

export const rolesApi = {
  list: async (): Promise<RoleRecord[]> => {
    try {
      const response = await callZohoAPI(ROLE_REPORT)
      return handleListResponse<RoleRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Roles', err)
    }
  },

  get: async (id: string): Promise<RoleRecord | null> => {
    try {
      const response = await callZohoAPI(`${ROLE_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Role', err, { id })
    }
  },

  create: async (data: Partial<Omit<RoleRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(ROLE_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Role', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<RoleRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${ROLE_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Role', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${ROLE_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Role', err, { id })
    }
  },
}

// ── Audiences CRUD ──

export const audiencesCreatorApi = {
  list: async (): Promise<AudienceRecord[]> => {
    try {
      const response = await callZohoAPI(`${AUDIENCE_REPORT}?field_config=all`)
      return handleListResponse<AudienceRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Audiences', err)
    }
  },

  get: async (id: string): Promise<AudienceRecord | null> => {
    try {
      const response = await callZohoAPI(
        `${AUDIENCE_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Audience', err, { id })
    }
  },

  findByAudienceId: async (
    audienceId: string,
  ): Promise<AudienceRecord | null> => {
    try {
      const all = await audiencesCreatorApi.list()
      return all.find((a) => a.Audience_ID === audienceId) || null
    } catch {
      return null
    }
  },

  create: async (data: Partial<Omit<AudienceRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(AUDIENCE_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Audience', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<AudienceRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${AUDIENCE_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Audience', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${AUDIENCE_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Audience', err, { id })
    }
  },
}

// ── Form Templates Library CRUD ──

export const formTemplatesLibraryApi = {
  list: async (): Promise<FormTemplateRecord[]> => {
    try {
      const response = await callZohoAPI(
        `${FORM_TEMPLATE_REPORT}?field_config=all`,
      )
      return handleListResponse<FormTemplateRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Form Templates Library', err)
    }
  },

  get: async (id: string): Promise<FormTemplateRecord | null> => {
    try {
      const response = await callZohoAPI(
        `${FORM_TEMPLATE_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Form Template', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<FormTemplateRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(FORM_TEMPLATE_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Form Template', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<FormTemplateRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_TEMPLATE_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Form Template', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_TEMPLATE_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Form Template', err, { id })
    }
  },
}

// ── Email Template Library CRUD ──

export const emailTemplateLibraryApi = {
  list: async (): Promise<EmailLibraryRecord[]> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_LIBRARY_REPORT}?field_config=all`,
      )
      return handleListResponse<EmailLibraryRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Email Template Library', err)
    }
  },

  get: async (id: string): Promise<EmailLibraryRecord | null> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_LIBRARY_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Email Library Template', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<EmailLibraryRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(EMAIL_LIBRARY_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Email Library Template', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<EmailLibraryRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_LIBRARY_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Email Library Template', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_LIBRARY_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Email Library Template', err, { id })
    }
  },
}

// ── Landing Pages CRUD ──

export const landingPagesApi = {
  list: async (): Promise<LandingPageRecord[]> => {
    try {
      const response = await callZohoAPI(
        `${LANDING_PAGE_REPORT}?field_config=all`,
      )
      return handleListResponse<LandingPageRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Landing Pages', err)
    }
  },

  get: async (id: string): Promise<LandingPageRecord | null> => {
    try {
      const response = await callZohoAPI(
        `${LANDING_PAGE_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Landing Page', err, { id })
    }
  },

  findBySlug: async (slug: string): Promise<LandingPageRecord | null> => {
    try {
      const all = await landingPagesApi.list()
      return all.find((p) => p.Url_Slug === slug) || null
    } catch {
      return null
    }
  },

  create: async (
    data: Partial<Omit<LandingPageRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(LANDING_PAGE_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Landing Page', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<LandingPageRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${LANDING_PAGE_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Landing Page', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${LANDING_PAGE_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Landing Page', err, { id })
    }
  },
}

// ── Form Submission Handler ──

export interface FormSubmissionData {
  newsletterId: string
  formFields: Record<string, string>
  team: string
  region?: string
  redirectUrl?: string
}

/** Generate a URL-safe confirmation token */
function generateConfirmationToken(): string {
  const timestamp = Date.now().toString(36)
  const randomPart = Array.from({ length: 16 }, () =>
    Math.floor(Math.random() * 36).toString(36),
  ).join('')
  return `${timestamp}_${randomPart}`
}

/**
 * Replace all {{variable}} placeholders in a string with actual values.
 * Works with any number of dynamic form fields.
 */
function renderTemplate(
  template: string,
  variables: Record<string, string>,
): string {
  let rendered = template
  for (const [key, value] of Object.entries(variables)) {
    // Replace both {{key}} and {{ key }} (with optional spaces)
    const regex = new RegExp(`\\{\\{\\s*${escapeRegex(key)}\\s*\\}\\}`, 'gi')
    rendered = rendered.replace(regex, value || '')
  }
  return rendered
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Build the full confirmation URL for a specific submission token.
 * Uses the app's own /confirm route as the intermediary.
 */
function buildConfirmationUrl(token: string): string {
  // Use the current app's origin for the confirmation endpoint
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://your-app-domain.com'
  return `${origin}/confirm?token=${encodeURIComponent(token)}`
}

/**
 * Render an email template with all variables replaced.
 * Handles: form fields, confirmation_link, team_name, and any custom variables.
 */
async function renderEmailForSubmission(params: {
  templateId: string
  formFields: Record<string, string>
  confirmationToken: string
  team: string
}): Promise<{ renderedHtml: string; renderedSubject: string } | null> {
  try {
    const template = await emailTemplatesApi.get(params.templateId)
    if (!template) return null

    // Build the variable map from form fields + system variables
    const variables: Record<string, string> = {
      ...params.formFields,
      confirmation_link: buildConfirmationUrl(params.confirmationToken),
      team_name: params.team,
    }

    const renderedHtml = renderTemplate(
      template.Email_HTML_Body || '',
      variables,
    )
    const renderedSubject = renderTemplate(
      template.Email_Subject || '',
      variables,
    )

    return { renderedHtml, renderedSubject }
  } catch (err) {
    console.error('Failed to render email template:', err)
    return null
  }
}

/**
 * Look up the email template linked to a form via Form Configurations.
 */
async function findTemplateForForm(formId: string): Promise<string | null> {
  try {
    const configs = await formConfigsApi.findByFormId(formId)
    if (configs.length === 0) return null
    return extractLookupId(configs[0].Email_Template) || null
  } catch {
    return null
  }
}

export async function handleFormSubmission(
  data: FormSubmissionData & { formId?: string },
): Promise<{ response: any; confirmationToken: string }> {
  const confirmationToken = generateConfirmationToken()

  const payload = {
    newsletter_id: data.newsletterId,
    ...data.formFields,
    submitted_at: new Date().toISOString(),
  }

  const submissionRecord: Record<string, any> = {
    Form_Payload: JSON.stringify(payload),
    Status: 'Not Subscribed',
    Newsletter_ID: data.newsletterId,
    Team: data.team,
    Region: data.region || '',
    Confirmation_Token: confirmationToken,
  }

  // Store the redirect URL so the /confirm page knows where to send the subscriber
  if (data.redirectUrl) {
    submissionRecord.Redirect_URL = data.redirectUrl
  }

  // Extract subscriber email from form fields (check common field names)
  const emailField =
    data.formFields.email ||
    data.formFields.Email ||
    data.formFields.email_address ||
    data.formFields.EMAIL ||
    ''
  if (emailField) {
    submissionRecord.Subscriber_Email = emailField
  }

  // Try to render the email template with actual values
  if (data.formId) {
    const templateId = await findTemplateForForm(data.formId)
    if (templateId) {
      const rendered = await renderEmailForSubmission({
        templateId,
        formFields: data.formFields,
        confirmationToken,
        team: data.team,
      })
      if (rendered) {
        submissionRecord.Rendered_Email_HTML = rendered.renderedHtml
        submissionRecord.Rendered_Email_Subject = rendered.renderedSubject
      }
    }
  }

  const response = await submissionsApi.create(submissionRecord)
  return { response, confirmationToken }
}

// ── Shared Helpers (exported) ──

export function extractLookupId(val: any): string {
  if (!val) return ''
  if (typeof val === 'object' && val.ID) return String(val.ID)
  if (typeof val === 'number') return String(val)
  if (typeof val === 'string') return val
  return ''
}

export function extractLookupDisplay(val: any): string {
  if (!val) return ''
  if (typeof val === 'object' && val.display_value) return val.display_value
  if (typeof val === 'object' && val.ID) return String(val.ID)
  if (typeof val === 'string') return val
  return ''
}
